import { useCallback, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardTitle,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  Skeleton,
} from '@design-system';
import { PageHeader } from '@/features/quote-and-apply-builder';
import { useCompositeTemplatesPage } from '@/features/quote-and-apply-builder/hooks/use-composite-templates-page';
import { TemplateConfigFormDialog } from '@/features/quote-and-apply-builder/components/template-config-form-dialog';

import {
  ArrowLeft,
  Clock,
  FileStack,
  Hash,
  InfoIcon,
  Layers,
  Pencil,
  Plus,
  Shield,
  Trash2,
  Users,
} from 'lucide-react';

function formatConditionSummary(conditional: TemplateConditional): string {
  if (!conditional?.rules || conditional.rules.length === 0) return 'Always included';
  const count = conditional.rules.length;
  return `${count} condition${count > 1 ? 's' : ''} (${conditional.logic})`;
}

export function CompositeTemplatesPage() {
  const {
    carrier,
    form,
    formId,
    questions,
    configs,
    isLoading,
    error,
    isDeletePending,
    configToDelete,
    isEmptyState,
    isDataLoaded,
    handleDeleteClick,
    handleConfirmDelete,
    handleDismissDelete,
    navigateBack,
  } = useCompositeTemplatesPage();

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PolicyApplicationTemplateConfiguration | null>(
    null,
  );

  const handleCreate = useCallback(() => {
    setEditingConfig(null);
    setIsFormDialogOpen(true);
  }, []);

  const handleEdit = useCallback((config: PolicyApplicationTemplateConfiguration) => {
    setEditingConfig(config);
    setIsFormDialogOpen(true);
  }, []);

  const handleFormDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setIsFormDialogOpen(false);
      setEditingConfig(null);
    }
  }, []);

  const pageTitle = form ? `Composite Templates - ${form.displayName}` : 'Composite Templates';

  const pageDescription = carrier
    ? `${carrier.carrierName}${form?.stateCode ? ` · ${form.stateCode}` : ''} · ${configs.length} template${configs.length !== 1 ? 's' : ''}`
    : undefined;

  return (
    <div className="mx-auto max-w-5xl px-6 pb-12 pt-6">
      <PageHeader
        title={pageTitle}
        description={pageDescription}
      >
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={navigateBack}
          >
            <ArrowLeft />
            Back
          </Button>
          <Button onClick={handleCreate}>
            <Plus />
            Add Template
          </Button>
        </div>
      </PageHeader>

      {isLoading && (
        <div className="flex flex-col gap-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card
              key={index}
              className="block p-4"
            >
              <div className="flex items-center gap-4">
                <Skeleton className="size-12 rounded-md" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-64" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <InfoIcon />
          <AlertTitle>Failed to load template configurations. Please try again.</AlertTitle>
        </Alert>
      )}

      {isEmptyState && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Layers />
            </EmptyMedia>
            <EmptyDescription>
              No composite templates configured{form ? ` for "${form.displayName}"` : ''}. Composite
              templates allow combining multiple DocuSign templates into a single envelope.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              size="sm"
              onClick={handleCreate}
            >
              <Plus />
              Create First Template
            </Button>
          </EmptyContent>
        </Empty>
      )}

      {isDataLoaded && (
        <div className="flex flex-col gap-4">
          {configs.map(config => (
            <Card
              key={config.id}
              className="group relative p-4 transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 text-primary flex size-14 shrink-0 items-center justify-center rounded-md">
                  <FileStack className="size-6" />
                </div>
                <div className="mt-1 flex flex-col gap-3">
                  <CardTitle>{config.templateName}</CardTitle>
                  <div className="text-foreground flex flex-wrap items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <Hash className="size-3.5" />
                      Sequence: {config.defaultSequence}
                    </span>
                    <Badge variant={config.isRequired ? 'default' : 'secondary'}>
                      {config.isRequired ? 'Required' : 'Optional'}
                    </Badge>
                    <Badge variant={config.status === 'active' ? 'default' : 'outline'}>
                      {config.status}
                    </Badge>
                    {config.updatedAt && (
                      <p className="flex items-center gap-1 text-sm">
                        <Clock className="size-3.5" />
                        Updated{' '}
                        {new Date(config.updatedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute right-2 top-2 flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEdit(config)}
                  title="Edit template configuration"
                >
                  <Pencil />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteClick(config)}
                  title="Delete template configuration"
                >
                  <Trash2 />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {formId && (
        <TemplateConfigFormDialog
          open={isFormDialogOpen}
          onOpenChange={handleFormDialogClose}
          formId={formId}
          questions={questions}
          config={editingConfig}
        />
      )}

      <AlertDialog
        open={!!configToDelete}
        onOpenChange={handleDismissDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{configToDelete?.templateName}&quot;? This will
              remove the template from the composite envelope. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletePending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeletePending}
            >
              {isDeletePending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
